import Sty, { Container } from "modules/Sty";
import { BrandsToBeAddedToOrganization, CurrentBrand } from "modules/Types";
import { CSSProperties, Fragment, useEffect, useState } from "react";
import { Divider, TextField } from "@material-ui/core";
import { AlertContainer } from "./AddUserModal/styles";
import { Body1, Body2 } from "components/shareable/Typography";
import { PlanType, PRO_MONTHLY_PLAN_PRICE, TEAM_MONTHLY_PLAN_PRICE, TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE, TEAM_PLAN_INCLUDED_PRO_ACCOUNTS } from "modules/Const";
import { adminCreateNewOrganizationWithTrial_api } from "api/organizationApi";
import { BrandUser, getBrandUsers_api } from "api/brandApi";
import Loader from 'elements/Loader';
import { Autocomplete } from "@mui/material";
import AdminSearchSelectorModal from "components/ProfileDropdown/AdminSearchSelectorModal";
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import Colors from "modules/Colors";
import ButtonSymphony from "elements/ButtonSymphony";
import useOrganization from "Hooks/useOrganization";
import SwitchSymphony from "elements/SwitchSymphony";
import InputSymphony from "elements/InputSymphony";
import CardSymphony from "elements/CardSymphony";
import { pluralize } from "modules/Utils";
import CustomAvatar from "components/ProfileDropdown/CustomAvatar";
import useCurrentUser from "Hooks/useCurrentUser";
import { useHistory } from "react-router-dom";
import DebugBox from "components/shareable/DebugBox";
const DEFAULT_INCLUDED_PRO_ACCOUNTS = TEAM_PLAN_INCLUDED_PRO_ACCOUNTS; // Number of included Pro accounts
const PRO_PROFILE_OVERAGE_PRICE = TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE; // Price for each additional Pro account

interface BrandToBeAdded {
    id: number;
    name: string;
    image: string;
    slug: string;
    selectedForPro: boolean;
    alreadyOnPro: boolean;
}

interface OrganizationNeedsSetupStateProps {
    title?: string;
    description?: string;
}

const OrganizationNeedsSetupState = ({
    title,
    description,
}: OrganizationNeedsSetupStateProps) => {
    // Used in Admin Setup flow to automatically view a created organization
    const history = useHistory()
    const { loadedBrands, setCurrentBrand, updateOrAddBrand } = useCurrentBrand() as BrandExistsState
    const { isSymphonyAdmin } = useCurrentUser()
    const { organization, getOrganization, completeOrganizationSetup, setOrganization } = useOrganization();

    /** Transform a CurrentBrand into a SelectableBrand with pro status and seat tracking */
    const formatToBrandToBeAdded = (brand: CurrentBrand): BrandToBeAdded => {
        const selectedForPro = brand.tier === PlanType.PRO
        return {
            id: brand.id,
            selectedForPro,
            name: brand.name!,
            image: brand.image!,
            slug: brand.slug!,
            alreadyOnPro: brand.tier === PlanType.PRO
        }
    }

    const initSelectableBrands = (): BrandToBeAdded[] => {
        if (isSymphonyAdmin) {
            return []
        }

        let selectableBrandsLocal: BrandToBeAdded[] = []

        if (loadedBrands?.length) {
            selectableBrandsLocal = loadedBrands.map((brand: CurrentBrand): BrandToBeAdded => {
                return formatToBrandToBeAdded(brand)
            })
        }

        return selectableBrandsLocal
    }

    // Organization Name
    const [nameValue, setNameValue] = useState<string>(isSymphonyAdmin ? '' : organization?.name || '')

    // Admin Setup Options:
    //  - admin setup lets an admin create a team on behalf of an organization or profile
    //  - they can select the main email of the user by default or enter an email address
    //  - the selected brand will be added by default to the team,
    //    and admins can search for additional brands to add to the team. they can also include the 
    //    selected user's Brands
    const [ownerEmail, setOwnerEmail] = useState<string>('')
    const [importedBrandUsersList, setImportedBrandsUsersList] = useState<BrandUser[]>([])
    const [usersLoading, setUsersLoading] = useState<boolean>(false)
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false)

    const [brandsToBeAdded, setBrandsToBeAdded] = useState<BrandToBeAdded[]>(initSelectableBrands())

    const brandsToBeUpgradedToProCount: number = brandsToBeAdded.filter((sb: BrandToBeAdded) => sb.selectedForPro).length
    const userHasOverage = Boolean(brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS > 0)

    // loading state
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const getProSeatCount = () => {
        return brandsToBeAdded.filter((sb: BrandToBeAdded) => sb.selectedForPro).length
    }

    const formatToBrandsToBeAdded = (brands: CurrentBrand[]): BrandToBeAdded[] => {
        return brands.map((brand: CurrentBrand) => formatToBrandToBeAdded(brand))
    }

    const getImportedBrandUsers = async (brandsToBeAddedLocal: BrandToBeAdded[]) => {
        setUsersLoading(true)
        try {
            /** Fetch users for all brands concurrently */
            const userResponses = await Promise.all(
                brandsToBeAddedLocal.map(brand => getBrandUsers_api({ brandId: brand.id }))
            )

            /** Combine and deduplicate users from all brands */
            const allUsers = userResponses.reduce((acc: BrandUser[], response) => {
                const brandUsers = response.users || []
                return [
                    ...acc,
                    ...brandUsers.filter(newUser =>
                        !acc.some(existingUser => existingUser.email === newUser.email)
                    )
                ]
            }, [])

            setImportedBrandsUsersList(allUsers)
        } catch (error) {
            console.error('Error fetching brand users:', error)
        } finally {
            setUsersLoading(false)
        }
    }

    const overageCalculator = () => {
        const overage = brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS
        if (overage > 0) {
            return overage * PRO_PROFILE_OVERAGE_PRICE
        }

        return 0
    }

    const savingsCalculator = () => {
        if (brandsToBeUpgradedToProCount < DEFAULT_INCLUDED_PRO_ACCOUNTS) {
            return 0
        }

        const PER_PROFILE_TEAM_PLAN_COST = TEAM_MONTHLY_PLAN_PRICE / DEFAULT_INCLUDED_PRO_ACCOUNTS;
        // Cost per profile under the team plan

        let savings = 0;

        if (brandsToBeUpgradedToProCount <= DEFAULT_INCLUDED_PRO_ACCOUNTS) {
            // Calculate savings per profile when total used seats are less than or equal to 5
            savings = (PRO_MONTHLY_PLAN_PRICE - PER_PROFILE_TEAM_PLAN_COST) * brandsToBeUpgradedToProCount;
        } else {
            // Calculate total savings when there are more than 5 profiles
            const baseSavings = (PRO_MONTHLY_PLAN_PRICE - PER_PROFILE_TEAM_PLAN_COST) * DEFAULT_INCLUDED_PRO_ACCOUNTS;
            const overageSeats = brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS;
            const overageSavings = (PRO_MONTHLY_PLAN_PRICE - TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE) * overageSeats;
            savings = baseSavings + overageSavings;
        }

        return Math.round(savings);
    }
    const handleSelectedForPro = (brand: BrandToBeAdded, selectedForPro: boolean) => {
        /** Update selected status and recalculate seat indices in a single pass */
        let proCount = 0;
        const updatedBrands = brandsToBeAdded.map((b: BrandToBeAdded) => {
            const isSelectedForPro = b.name === brand.name ? selectedForPro : b.selectedForPro;
            if (isSelectedForPro) proCount++;

            return {
                ...b,
                selectedForPro: isSelectedForPro,
                proSeatIndex: isSelectedForPro ? proCount : 0
            };
        });

        setBrandsToBeAdded(updatedBrands);
    }

    const handleRemoveBrand = (brand: BrandToBeAdded) => {
        const newSelectableBrands = brandsToBeAdded.filter((o: BrandToBeAdded) => o.name !== brand.name);
        setBrandsToBeAdded(newSelectableBrands);
    }

    const setAllSelectedForPro = (selectedForPro: boolean) => {
        const newSelectableBrands = brandsToBeAdded.map((brand: BrandToBeAdded) => {
            return {
                ...brand,
                selectedForPro: brand.alreadyOnPro ? true : selectedForPro
            }
        }).map((brand: BrandToBeAdded, idx: number) => {
            if (brand.selectedForPro) {
                return {
                    ...brand,
                    proSeatIndex: idx + 1
                }
            }
            return brand
        })
        setBrandsToBeAdded(newSelectableBrands)
    }

    const renderSelectableBrands = () => {
        // Filter and map over brands selected for Pro
        const proSelectedBrands = brandsToBeAdded.filter(brand => brand.selectedForPro)
            .map((brand, index) => ({
                ...brand,
                proSeatIndex: index + 1,
                overageSeat: index >= DEFAULT_INCLUDED_PRO_ACCOUNTS // true if it's beyond the included accounts
            }));

        return (
            <div className="artistListOrganizationNeedsSetupState" style={sty.artistList}>
                {brandsToBeAdded.map((brand: BrandToBeAdded, index: number) => {
                    // Find the brand in the proSelectedBrands array to get the correct proSeatIndex
                    return (
                        <Fragment key={`${brand.slug}-${index}`}>
                            {renderBrandRow(brand)}
                        </Fragment>
                    )
                })}
            </div>
        )
    }

    const handleFinishSetup = async () => {
        try {
            setIsLoading(true)
            // NOTE: these are basically CurrentBrand[] but with the upgradeToPro flag
            const brandsToBeAddedToOrganization: BrandsToBeAddedToOrganization[] = brandsToBeAdded.map((sb: BrandToBeAdded) => {
                return {
                    brandId: sb.id,
                    currentTier: sb.alreadyOnPro ? "pro" : "free",
                    upgradeToPro: !sb.alreadyOnPro && sb.selectedForPro,
                }
            })

            // if an admin is setting up the organization, we need to create a new organization with a trial
            // otherwise handle the default flow
            if (isSymphonyAdmin) {
                const finishSetup = await adminCreateNewOrganizationWithTrial_api(nameValue, ownerEmail, brandsToBeAddedToOrganization as any) // TODO XXX: check about 'any', this is a type check error between TeamMember.brands, and CurrentBrand[]; we should figure out a consisent way to deal with this, since it comes around a lot, perhaps TeamMember.brands should just be brandIds?  then we reference in loadedBrands?  not sure
                if (finishSetup) {
                    const {
                        primaryOwner, // NOTE: not needed
                        organization
                    } = finishSetup

                    // NOTE: if it's an admin, getOrganization will automatically call updateOrAddBrands so it gets all the brands into the loadedBrands array and dict
                    const newOrganization = await getOrganization(organization!.id)
                    const firstBrand = newOrganization!.brands[0]
                    setCurrentBrand(firstBrand)
                    setOrganization(newOrganization!)
                    history.push('/team')
                }
            } else {
                await completeOrganizationSetup(nameValue, brandsToBeAddedToOrganization)
            }
        } catch (error) {
            console.error("error: ", error)
        } finally {
            setIsLoading(false)
        }
    }

    const showTeamSummary = () => {
        return (
            <AlertContainer className="teamSummary" type="success" style={{ marginTop: 18 }}>
                <Body1>
                    You have {TEAM_PLAN_INCLUDED_PRO_ACCOUNTS} Pro accounts included in your Team plan. You can upgrade additional profiles to Pro for ${TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE} / month.
                </Body1>
                <Container padding="12px 0px" display="flex" flexDirection="column" gap="4px">
                    <Body2>
                        Selected Pro Accounts: {brandsToBeUpgradedToProCount}
                    </Body2>
                    {userHasOverage &&
                        <Body2>
                            Additional Pro Accounts: ${overageCalculator()} / month ({brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS < 0 ? 0 : brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS} {pluralize('profile', brandsToBeUpgradedToProCount - DEFAULT_INCLUDED_PRO_ACCOUNTS)})
                        </Body2>
                    }
                </Container>
                {savingsCalculator() > 0 &&
                    <Body2 color={Colors.purple}>
                        You're saving ${savingsCalculator()} / month with a Team plan vs. buying individual Pro accounts.
                    </Body2>
                }
            </AlertContainer>
        )
    }

    const renderAdminSelectEmailSection = () => {
        let selectOrLoader = null;

        if (usersLoading) {
            selectOrLoader = <Loader />;
        } else {
            selectOrLoader = (
                <Autocomplete
                    freeSolo
                    sx={{
                        '& .MuiAutocomplete-input': {
                            fontFamily: 'DIN'
                        },
                    }}
                    options={importedBrandUsersList.map(user => user.email)}
                    value={ownerEmail}
                    onChange={(_, newValue) => {
                        setOwnerEmail(newValue || '');
                    }}
                    onInputChange={(_, newInputValue) => {
                        setOwnerEmail(newInputValue);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select or enter an email"
                            type="email"
                            fullWidth
                        />
                    )}
                />
            );
        }

        return (
            <Container className="adminSetupOwnerEmail" marginTop="18px" display="flex" flexDirection="column">
                <Body1 style={{ marginBottom: 8 }}>
                    What's the team owner's email?
                </Body1>
                {selectOrLoader}
            </Container>
        );
    }

    const showHelperText = (alreadyOnPro: boolean, selectedForPro: boolean) => {
        if (alreadyOnPro) {
            if (selectedForPro) {
                return "This profile will remain on Pro."
            } else {
                return "This profile will be downgraded to the Free tier."
            }
        } else if (selectedForPro) {
            return "This profile will be upgraded to Pro."
        } else {
            return "This profile is on the Free tier."
        }
    }

    const renderTeamNameSection = () => {
        return (
            <Container className="setupStatusInputContainer" marginTop="18px" display="flex" flexDirection="column">
                <Body1 className="setupStatusInputLabel" style={{ marginBottom: 8 }}>
                    What do you want to call your team?
                </Body1>
                <InputSymphony
                    className="setupStatusInput"
                    value={nameValue}
                    placeholder="Enter your team's name"
                    onChange={(valueText) => setNameValue(valueText)}
                    maxLength={90}
                />
            </Container>
        )
    }

    const renderBrandRow = (brand: BrandToBeAdded) => {
        const {
            name,
            image,
            selectedForPro,
            alreadyOnPro,
        } = brand;

        let canRemove = false
        if (isSymphonyAdmin) {
            canRemove = true
        }

        return (
            <Container
                className="brandRowOrganizationNeedsSetupState"
                borderBottom="1px solid #E5E7EB"
                padding="12px"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                backgroundColor={selectedForPro ? `#f7eeff` : 'white'}
                style={{
                    transition: `all 0.2s ease`
                }}
            >
                <Container
                    className="brandRowLeftInfo"
                    display="flex"
                    flexDirection="row"
                    gap="8px"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CustomAvatar
                        className="fromBrandRowLeftInfoImage"
                        size={54}
                        image={image}
                        isBorder={true}
                    />
                    <Container className="brandRowLeftInfoText" display="flex" flexDirection="column" gap="0px">
                        <div
                            className="brandRowLeftInfoTextName"
                            style={{
                                fontSize: 18,
                                fontWeight: 500
                            }}
                        >
                            {name}
                        </div>
                        <div
                            className="brandRowLeftInfoTextHelperText"
                            style={{
                                fontSize: 14,
                                color: Colors.greyDark
                            }}
                        >
                            {showHelperText(alreadyOnPro, selectedForPro)}
                        </div>
                        {/* TODO: add back in when we have a way to remove artists from the team */}
                        {canRemove &&
                            <ButtonSymphony
                                className="organizationPageRemoveArtistButton"
                                onClick={() => handleRemoveBrand(brand)}
                                variant="text"
                                color="red"
                                size="small"
                            >
                                Remove from Team
                            </ButtonSymphony>
                        }
                    </Container>
                </Container>
                <SwitchSymphony
                    className="organizationPageArtistSwitch"
                    disabled={alreadyOnPro}
                    checked={selectedForPro}
                    onChange={() => handleSelectedForPro(brand, !selectedForPro)}
                    label={alreadyOnPro ? 'Pro Profile' : 'Upgrade to Pro'}
                    labelPlacement="left"
                />
            </Container>
        )
    }

    const renderSelectedBrandsSection = () => {
        return (
            <Container className="selectedBrandsSection" marginTop="18px" display="flex" flexDirection="column" gap="8px">
                <Container width={'100%'}
                    display="flex"
                    flexDirection="column"
                    gap="4px"
                    style={{
                        marginTop: 16
                    }}
                >
                    <div className="selectProfilesToAddToTeam" style={Sty.text18bold}>
                        Select the profiles you want to add to your Team and upgrade to Pro.
                    </div>
                    <Body2 color={Colors.greyDark} style={{ marginBottom: 8 }}>
                        You can easily add and upgrade additional profiles after setup.
                    </Body2>
                    {brandsToBeAdded.length > 2 &&
                        <Container display="flex" flexDirection="row" alignItems="center">
                            <ButtonSymphony
                                className="upgradeAllToProButton"
                                onClick={() => setAllSelectedForPro(true)}
                                variant="outlined"
                            >
                                Upgrade All to Pro
                            </ButtonSymphony>
                        </Container>
                    }
                </Container>

                {isSymphonyAdmin &&
                    <ButtonSymphony
                        className="importFromAdminSearchButton"
                        variant="text"
                        onClick={() => {
                            setOpenAdminSelector(true)
                        }}
                    >
                        Import from Admin Search
                    </ButtonSymphony>
                }

                <Divider />
                {renderSelectableBrands()}
            </Container>
        )
    }

    return (
        <Container
            className="organizationNeedsSetupStateComponent"
            display="flex"
            flexDirection="column"
            gap="16px"
        >
            <CardSymphony
                className="setupStatusHeader"
                style={{ padding: 0 }}
                title={title || "Setup your Team"}
                subtitle={description || `You can manage unlimited profiles through your Team, and you have ${TEAM_PLAN_INCLUDED_PRO_ACCOUNTS} Pro accounts included by default.`}
            />
            <Container className="setupStatusInputContainer" display="flex" flexDirection="column">
                {renderTeamNameSection()}

                {isSymphonyAdmin && renderAdminSelectEmailSection()}

                {renderSelectedBrandsSection()}

                {showTeamSummary()}
                <ButtonSymphony
                    className="finishSetupButton"
                    onClick={handleFinishSetup}
                    disabled={isLoading || !nameValue || nameValue.trim().length < 3 || (isSymphonyAdmin && !ownerEmail)}
                    isLoading={isLoading}
                    style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: 24 }}
                >
                    Finish Setup
                </ButtonSymphony>
                {isSymphonyAdmin &&
                    <AdminSearchSelectorModal
                        mode="select"
                        onImportSelected={(brands) => {
                            const brandsLocal = [...brands, ...brandsToBeAdded.filter(sb => !brands.some(b => b.id === sb.id))]
                            const updatedBrandsToBeAdded = formatToBrandsToBeAdded(brandsLocal as CurrentBrand[])
                            getImportedBrandUsers(updatedBrandsToBeAdded)
                            setBrandsToBeAdded(updatedBrandsToBeAdded)
                        }}
                        open={openAdminSelector}
                        onClose={() => setOpenAdminSelector(false)}
                    />
                }
            </Container>
        </Container>
    )
}

const sty: Record<string, CSSProperties> = {
    brandImage: {
        width: 60,
        height: 60,
        borderRadius: '100%'
    },
    artistList: {
        overflow: 'auto'
    }
}

export default OrganizationNeedsSetupState